import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Loader2, Crown } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) pollStatus(sessionId, 0);
    else setStatus('error');
  }, [sessionId]);

  const pollStatus = async (sid, attempts) => {
    if (attempts >= 6) { setStatus('timeout'); return; }
    try {
      const res = await fetch(`${API_BASE}/api/stripe/checkout-status/${sid}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setStatus('paid');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.plan = 'premium';
        localStorage.setItem('user', JSON.stringify(user));
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollStatus(sid, attempts + 1), 2000);
      }
    } catch (e) {
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-b from-[#f0f7fa] to-white">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="mx-auto mb-4 animate-spin text-[#205F79]" size={48} />
              <h2 className="text-xl font-semibold mb-2">Vérification du paiement...</h2>
              <p className="text-sm text-muted-foreground">Veuillez patienter quelques instants.</p>
            </>
          )}
          {status === 'paid' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Paiement confirmé !</h2>
              <p className="text-sm text-muted-foreground mb-2">Votre abonnement Premium est maintenant actif.</p>
              <div className="flex items-center justify-center gap-2 text-amber-600 font-medium mb-6">
                <Crown size={18} /> Plan Premium activé
              </div>
              <Button className="w-full bg-[#205F79] hover:bg-[#184a5e]" onClick={() => navigate('/')}>
                Accéder à mon espace
              </Button>
            </>
          )}
          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 text-2xl font-bold">!</span>
              </div>
              <h2 className="text-xl font-semibold mb-2">
                {status === 'expired' ? 'Session expirée' : 'Erreur de vérification'}
              </h2>
              <p className="text-sm text-muted-foreground mb-6">Veuillez réessayer ou contacter le support.</p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/subscription')}>
                Retour à l'abonnement
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SubscriptionCancel = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-b from-[#f0f7fa] to-white">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Paiement annulé</h2>
          <p className="text-sm text-muted-foreground mb-6">Votre paiement a été annulé. Vous pouvez réessayer à tout moment.</p>
          <Button className="w-full bg-[#205F79] hover:bg-[#184a5e]" onClick={() => navigate('/subscription')}>
            Retour à l'abonnement
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export { SubscriptionSuccess, SubscriptionCancel };
